/**
 * TimelineView Component
 * Visualizes spot occupancy bitmaps over time
 */

import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import * as React from "react";
import { bucketToTimeString } from "../bitmap";
import { isSpotEnabled } from "../spotFiltering";
import { isSpotInStagingZone } from "../stagingArea";
import { LogEntry, ParkingRequest, Placement, Spot, Zone } from "../types";
import { formatSizeCategories } from "../utils/formatSizeCategory";
import { DecisionLogDisplay } from "./DecisionLogDisplay";
import { UnplacedAircraftDialog } from "./UnplacedAircraftDialog";

interface TimelineViewProps {
  spots: Spot[];
  aircraft: ParkingRequest[];
  placement: Placement;
  log: LogEntry[];
  zones?: Zone[];
  horizonHours?: number;
  startTime?: Date;
  spotIdToName?: Record<string, string>;
}

/**
 * Get a color for an aircraft (consistent across timeline)
 *
 * Generated 40 visually distinct colors using HSL color space to avoid repetition
 * when displaying large numbers of aircraft (e.g., TEB schedule with 36 aircraft)
 */
const COLORS = [
  "#e63946",
  "#f77f00",
  "#fcbf49",
  "#06d6a0",
  "#118ab2",
  "#073b4c",
  "#9d4edd",
  "#ff006e",
  "#8338ec",
  "#3a86ff",
  "#fb5607",
  "#ffbe0b",
  "#38b000",
  "#00b4d8",
  "#0077b6",
  "#c9184a",
  "#ff4d6d",
  "#ff758f",
  "#06ffa5",
  "#03045e",
  "#7209b7",
  "#b5179e",
  "#f72585",
  "#4361ee",
  "#4cc9f0",
  "#e85d04",
  "#dc2f02",
  "#d00000",
  "#9d0208",
  "#6a040f",
  "#588157",
  "#3a5a40",
  "#344e41",
  "#a3b18a",
  "#dad7cd",
  "#03071e",
  "#370617",
  "#6a040f",
  "#9d0208",
  "#d00000",
];

function getAircraftColor(
  aircraftId: string,
  allAircraft: ParkingRequest[]
): string {
  const index = allAircraft.findIndex((ac) => ac.id === aircraftId);
  // With 40 colors, should cover most scenarios without repetition
  return COLORS[index % COLORS.length];
}

/**
 * Determine if a specific bucket is in a staging segment
 * Returns segment index if staging, null if permanent or single-spot
 */
function getStagingSegmentInfo(
  spotId: string,
  bucket: number,
  aircraft: ParkingRequest,
  placement: Placement,
  spots: Spot[]
): {
  isStaging: boolean;
  segmentIndex: number | null;
  segmentType: "arrival" | "permanent" | "departure" | null;
} {
  const placed = placement[aircraft.id];
  if (!placed || !("segments" in placed)) {
    // Single-spot placement - not staging
    return { isStaging: false, segmentIndex: null, segmentType: null };
  }

  // Find which segment contains this bucket
  const segmentIndex = placed.segments.findIndex(
    (seg) =>
      seg.spotId === spotId &&
      bucket >= seg.startBucket &&
      bucket < seg.endBucket
  );

  if (segmentIndex === -1) {
    return { isStaging: false, segmentIndex: null, segmentType: null };
  }

  const segment = placed.segments[segmentIndex];
  const spot = spots.find((s) => s.id === segment.spotId);

  if (!spot) {
    return { isStaging: false, segmentIndex, segmentType: null };
  }

  const isStaging = isSpotInStagingZone(spot);

  // Determine segment type based on position and staging status
  let segmentType: "arrival" | "permanent" | "departure" | null = null;
  if (isStaging) {
    // Staging segments: first is arrival, last is departure
    if (segmentIndex === 0) {
      segmentType = "arrival";
    } else if (segmentIndex === placed.segments.length - 1) {
      segmentType = "departure";
    }
  } else {
    segmentType = "permanent";
  }

  return { isStaging, segmentIndex, segmentType };
}

/**
 * Extract unique lane IDs and names from spots' egress options
 * Returns array of {laneId, laneName} objects
 */
function extractUniqueLanes(
  spots: Spot[]
): Array<{ laneId: string; laneName: string }> {
  const laneMap = new Map<string, string>();

  for (const spot of spots) {
    if (spot.egressOptions) {
      for (const option of spot.egressOptions) {
        if (!laneMap.has(option.laneId)) {
          laneMap.set(option.laneId, option.laneName);
        }
      }
    }
  }

  return Array.from(laneMap.entries()).map(([laneId, laneName]) => ({
    laneId,
    laneName,
  }));
}

export const TimelineView: React.FC<TimelineViewProps> = ({
  spots,
  aircraft,
  placement,
  log,
  zones = [],
  horizonHours = 24,
  startTime = new Date(new Date().setHours(0, 0, 0, 0)),
  spotIdToName,
}) => {
  // State for unplaced aircraft dialog
  const [unplacedDialogOpen, setUnplacedDialogOpen] = React.useState(false);
  const [selectedBucket, setSelectedBucket] = React.useState<number>(0);

  // Calculate total slots (always 15-minute buckets for data accuracy)
  const totalSlots = React.useMemo(() => horizonHours * 4, [horizonHours]);

  // Dynamic display sampling to prevent DOM bloat
  // Data stays at 15-minute resolution, but we only render subset for display
  // 1-2 days: Show every bucket (15-minute precision)
  // 3-7 days: Show every 4th bucket (hourly precision, 4× fewer cells)
  // 8+ days: Show every 8th bucket (2-hour precision, 8× fewer cells)
  const displaySamplingRate = React.useMemo(() => {
    if (horizonHours <= 48) return 1; // Every bucket (15-min)
    if (horizonHours <= 168) return 4; // Every 4th bucket (hourly)
    return 8; // Every 8th bucket (2-hour)
  }, [horizonHours]);

  // Show sampled buckets for display (memoized)
  const buckets = React.useMemo(
    () =>
      Array.from({ length: totalSlots }, (_, i) => i).filter(
        (b) => b % displaySamplingRate === 0
      ),
    [totalSlots, displaySamplingRate]
  );

  // Determine if we need day labels (multi-day scenarios)
  const showDayLabels = horizonHours > 24;

  // Extract unique lanes from spots for lane utilization rows and sort by name (memoized)
  const lanes = React.useMemo(
    () =>
      extractUniqueLanes(spots).sort((a, b) =>
        a.laneName.localeCompare(b.laneName, undefined, { numeric: true })
      ),
    [spots]
  );

  // Sort spots by display name for better visual organization (memoized)
  const sortedSpots = React.useMemo(
    () =>
      [...spots].sort((a, b) => {
        const nameA = spotIdToName?.[a.id] || a.id;
        const nameB = spotIdToName?.[b.id] || b.id;
        return nameA.localeCompare(nameB, undefined, { numeric: true });
      }),
    [spots, spotIdToName]
  );

  // Pre-compute ALL timeline data in a single pass to eliminate race conditions
  // This ensures all cells read from the same snapshot of data
  const timelineData = React.useMemo(() => {
    // Map: `${spotId}-${bucket}` → aircraft occupying that spot at that time
    const aircraftAtSpotMap = new Map<string, ParkingRequest>();

    // Map: `${spotId}-${bucket}` → blocking information (if blocked)
    const blockingInfoMap = new Map<
      string,
      { blockedBy: ParkingRequest; reason: string }
    >();

    // Array: utilization data per bucket
    const utilizationData: Array<{ count: number; percentage: number }> = [];

    // Array: unplaced aircraft per bucket
    const unplacedData: Array<ParkingRequest[]> = [];

    // Map: `${laneId}-${bucket}` → aircraft departing on that lane at that time
    const laneData = new Map<string, ParkingRequest[]>();

    // Map: `${spotId}-${aircraftId}` → block span for tail number display
    const blockSpans = new Map<
      string,
      { start: number; middle: number; end: number }
    >();

    // Pass 1: Build aircraft placement map
    for (const ac of aircraft) {
      const placed = placement[ac.id];
      if (!placed) continue;

      if ("segments" in placed) {
        // Segmented placement
        for (const segment of placed.segments) {
          for (let b = segment.startBucket; b < segment.endBucket; b++) {
            aircraftAtSpotMap.set(`${segment.spotId}-${b}`, ac);
          }
        }
      } else {
        // Single placement
        for (let b = ac.arrivalBucket; b < ac.departureBucket; b++) {
          aircraftAtSpotMap.set(`${placed.spotId}-${b}`, ac);
        }
      }
    }

    // Pass 2: Build blocking info map
    for (const spot of spots) {
      for (let bucket = 0; bucket < totalSlots; bucket++) {
        const acAtSpot = aircraftAtSpotMap.get(`${spot.id}-${bucket}`);
        if (acAtSpot) continue; // Already occupied, not blocked

        // Check if blocked by another spot
        for (const otherSpot of spots) {
          const blocksThisSpot = (otherSpot.egressOptions || [])
            .some((option) => option.blocksSpotIds.includes(spot.id));
          if (!blocksThisSpot) continue;

          const blockingAircraft = aircraftAtSpotMap.get(
            `${otherSpot.id}-${bucket}`
          );
          if (!blockingAircraft) continue;

          if (bucket < blockingAircraft.departureBucket) {
            blockingInfoMap.set(`${spot.id}-${bucket}`, {
              blockedBy: blockingAircraft,
              reason: `Blocked by ${blockingAircraft.tail_number} at ${
                otherSpot.id
              } (departs ${bucketToTimeString(
                blockingAircraft.departureBucket
              )})`,
            });
            break; // Only need one blocking reason
          }
        }
      }
    }

    // Pass 3: Build utilization data
    for (let bucket = 0; bucket < totalSlots; bucket++) {
      let occupiedCount = 0;
      for (const spot of spots) {
        if (aircraftAtSpotMap.has(`${spot.id}-${bucket}`)) {
          occupiedCount++;
        }
      }
      const percentage =
        spots.length > 0 ? Math.round((occupiedCount / spots.length) * 100) : 0;
      utilizationData.push({ count: occupiedCount, percentage });
    }

    // Pass 4: Build unplaced aircraft data
    for (let bucket = 0; bucket < totalSlots; bucket++) {
      const unplaced = aircraft.filter((ac) => {
        if (placement[ac.id]) return false;
        return bucket >= ac.arrivalBucket && bucket < ac.departureBucket;
      });
      unplacedData.push(unplaced);
    }

    // Pass 5: Build lane departure data
    for (const lane of lanes) {
      for (let bucket = 0; bucket < totalSlots; bucket++) {
        const departing: ParkingRequest[] = [];

        for (const ac of aircraft) {
          const placed = placement[ac.id];
          if (!placed || ac.departureBucket !== bucket) continue;

          let spotId: string;
          if ("segments" in placed) {
            spotId = placed.segments[placed.segments.length - 1].spotId;
          } else {
            spotId = placed.spotId;
          }

          const spot = spots.find((s) => s.id === spotId);
          if (spot && spot.egressOptions) {
            const hasLane = spot.egressOptions.some(
              (option) =>
                option.laneId === lane.laneId || option.laneName === lane.laneId
            );
            if (hasLane) {
              departing.push(ac);
            }
          }
        }

        if (departing.length > 0) {
          laneData.set(`${lane.laneId}-${bucket}`, departing);
        }
      }
    }

    // Pass 6: Build block spans for tail number display
    for (const spot of sortedSpots) {
      const blocks = new Map<string, { start: number; end: number }>();

      // Find continuous blocks for each aircraft
      for (let bucket = 0; bucket < totalSlots; bucket++) {
        const ac = aircraftAtSpotMap.get(`${spot.id}-${bucket}`);
        if (!ac) continue;

        const key = ac.id;
        if (!blocks.has(key)) {
          blocks.set(key, { start: bucket, end: bucket });
        } else {
          const block = blocks.get(key)!;
          if (bucket === block.end + 1) {
            // Continuous block
            block.end = bucket;
          } else {
            // New block (gap in between) - finalize previous and start new
            const span = block.end - block.start + 1;
            const middle = block.start + Math.floor(span / 2);
            blockSpans.set(`${spot.id}-${key}`, {
              start: block.start,
              middle,
              end: block.end,
            });
            blocks.set(key, { start: bucket, end: bucket });
          }
        }
      }

      // Finalize remaining blocks
      for (const [acId, block] of blocks.entries()) {
        const span = block.end - block.start + 1;
        const middle = block.start + Math.floor(span / 2);
        blockSpans.set(`${spot.id}-${acId}`, {
          start: block.start,
          middle,
          end: block.end,
        });
      }
    }

    return {
      aircraftAtSpotMap,
      blockingInfoMap,
      utilizationData,
      unplacedData,
      laneData,
      blockSpans,
    };
  }, [spots, aircraft, placement, totalSlots, lanes, sortedSpots]);

  return (
    <Paper data-testid="timeline-view" elevation={1} sx={{ p: 1.5 }}>
      <Typography variant="h6" gutterBottom>
        Spot Occupancy Timeline ({horizonHours}h window)
      </Typography>

      <TableContainer sx={{ maxHeight: 600 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            {showDayLabels ? (
              <>
                {/* Day row - shows "Day 1", "Day 2", etc. spanning 24 hours each */}
                <TableRow>
                  <TableCell
                    rowSpan={2}
                    sx={{
                      fontWeight: "bold",
                      bgcolor: "grey.100",
                      position: "sticky",
                      left: 0,
                      zIndex: 3,
                      minWidth: "60px",
                    }}
                  >
                    Zone
                  </TableCell>
                  <TableCell
                    rowSpan={2}
                    sx={{
                      fontWeight: "bold",
                      bgcolor: "grey.100",
                      position: "sticky",
                      left: "60px",
                      zIndex: 3,
                      minWidth: "105px",
                    }}
                  >
                    Spot
                  </TableCell>
                  {(() => {
                    const days = Math.ceil(horizonHours / 24);
                    return Array.from({ length: days }, (_, dayIndex) => {
                      // Each day spans 96 buckets (24 hours × 4 buckets/hour)
                      const bucketsPerDay = 24 * 4;
                      const dayStartBucket = dayIndex * bucketsPerDay;
                      const dayEndBucket = Math.min(
                        (dayIndex + 1) * bucketsPerDay,
                        totalSlots
                      );

                      // Calculate how many DISPLAYED buckets fall within this day
                      // This accounts for display sampling (hourly/2-hour precision)
                      const displayedBucketsInDay = buckets.filter(
                        (b) => b >= dayStartBucket && b < dayEndBucket
                      ).length;

                      return (
                        <TableCell
                          key={`day-${dayIndex}`}
                          colSpan={displayedBucketsInDay}
                          align="center"
                          sx={{
                            fontWeight: "bold",
                            bgcolor: "grey.200",
                            borderRight: "2px solid rgba(0, 0, 0, 0.3)",
                            fontSize: "0.56rem",
                            padding: "3px",
                          }}
                        >
                          Day {dayIndex + 1}
                        </TableCell>
                      );
                    });
                  })()}
                </TableRow>
                {/* Time row - shows hourly times */}
                <TableRow>
                  {buckets.map((bucket) => (
                    <TableCell
                      key={bucket}
                      align="center"
                      sx={{
                        fontWeight: "bold",
                        bgcolor: "grey.100",
                        width: "22px",
                        maxWidth: "22px",
                        minWidth: "22px",
                        fontSize: "0.525rem",
                        padding: "3px",
                        borderRight:
                          bucket % 96 === 95
                            ? "2px solid rgba(0, 0, 0, 0.3)"
                            : "1px solid rgba(0, 0, 0, 0.2)",
                        overflow: "hidden",
                      }}
                    >
                      {bucketToTimeString(bucket, startTime, false)}
                    </TableCell>
                  ))}
                </TableRow>
              </>
            ) : (
              // Single-day view - just one row with times
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    bgcolor: "grey.100",
                    position: "sticky",
                    left: 0,
                    zIndex: 3,
                    minWidth: "60px",
                  }}
                >
                  Zone
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    bgcolor: "grey.100",
                    position: "sticky",
                    left: "60px",
                    zIndex: 3,
                    minWidth: "105px",
                  }}
                >
                  Spot
                </TableCell>
                {buckets.map((bucket) => (
                  <TableCell
                    key={bucket}
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      bgcolor: "grey.100",
                      width: "22px",
                      maxWidth: "22px",
                      minWidth: "22px",
                      fontSize: "0.525rem",
                      padding: "3px",
                      borderRight: "1px solid rgba(0, 0, 0, 0.2)",
                      overflow: "hidden",
                    }}
                  >
                    {displaySamplingRate === 1 && bucket % 4 !== 0
                      ? ""
                      : bucketToTimeString(bucket, startTime, false)}
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {/* Utilization Row */}
            <TableRow>
              <TableCell
                colSpan={2}
                component="th"
                scope="row"
                sx={{
                  fontWeight: "bold",
                  bgcolor: "white",
                  position: "sticky",
                  left: 0,
                  zIndex: 1,
                  color: "primary.main",
                  minWidth: "165px",
                }}
              >
                Utilization
              </TableCell>
              {buckets.map((bucket) => {
                const { percentage } = timelineData.utilizationData[bucket];

                // Color based on utilization percentage
                let bgcolor = "transparent";
                let color = "text.disabled";
                if (percentage >= 70) {
                  bgcolor = "success.100";
                  color = "success.dark";
                } else if (percentage >= 40) {
                  bgcolor = "warning.100";
                  color = "warning.dark";
                } else if (percentage > 0) {
                  bgcolor = "grey.100";
                  color = "text.secondary";
                }

                return (
                  <TableCell
                    key={bucket}
                    align="center"
                    sx={{
                      bgcolor,
                      color,
                      fontSize: "0.525rem",
                      fontFamily: "monospace",
                      fontWeight: percentage > 0 ? "bold" : "normal",
                      padding: "3px",
                      width: "22px",
                      maxWidth: "22px",
                      minWidth: "22px",
                      borderRight: "1px solid rgba(0, 0, 0, 0.2)",
                      overflow: "hidden",
                    }}
                  >
                    {percentage > 0 ? `${percentage}%` : "—"}
                  </TableCell>
                );
              })}
            </TableRow>

            {/* Placed Aircraft Row */}
            <TableRow>
              <TableCell
                colSpan={2}
                component="th"
                scope="row"
                sx={{
                  fontWeight: "bold",
                  bgcolor: "white",
                  position: "sticky",
                  left: 0,
                  zIndex: 1,
                  color: "primary.main",
                  minWidth: "165px",
                }}
              >
                Placed
              </TableCell>
              {buckets.map((bucket) => {
                const { count, percentage } =
                  timelineData.utilizationData[bucket];

                // Color based on utilization percentage (darker than Utilization row)
                let bgcolor = "transparent";
                let color = "text.disabled";
                if (percentage >= 70) {
                  bgcolor = "success.200";
                  color = "success.dark";
                } else if (percentage >= 40) {
                  bgcolor = "warning.200";
                  color = "warning.dark";
                } else if (count > 0) {
                  bgcolor = "grey.200";
                  color = "text.primary";
                }

                return (
                  <TableCell
                    key={bucket}
                    align="center"
                    sx={{
                      bgcolor,
                      color,
                      fontSize: "0.525rem",
                      fontFamily: "monospace",
                      fontWeight: count > 0 ? "bold" : "normal",
                      padding: "3px",
                      width: "22px",
                      maxWidth: "22px",
                      minWidth: "22px",
                      borderRight: "1px solid rgba(0, 0, 0, 0.2)",
                      overflow: "hidden",
                    }}
                  >
                    {count > 0 ? count : "—"}
                  </TableCell>
                );
              })}
            </TableRow>

            {/* Unplaced Aircraft Row */}
            <TableRow>
              <TableCell
                colSpan={2}
                component="th"
                scope="row"
                sx={{
                  fontWeight: "bold",
                  bgcolor: "white",
                  position: "sticky",
                  left: 0,
                  zIndex: 1,
                  color: "error.main",
                  minWidth: "165px",
                }}
              >
                Unplaced
              </TableCell>
              {buckets.map((bucket) => {
                const unplacedAircraft = timelineData.unplacedData[bucket];
                const count = unplacedAircraft.length;
                const tailNumbers = unplacedAircraft
                  .map((ac) => ac.tail_number)
                  .join(", ");

                const handleClick = () => {
                  if (count > 0) {
                    setSelectedBucket(bucket);
                    setUnplacedDialogOpen(true);
                  }
                };

                return (
                  <TableCell
                    key={bucket}
                    align="center"
                    sx={{
                      bgcolor: count > 0 ? "error.100" : "transparent",
                      color: count > 0 ? "error.main" : "text.disabled",
                      fontSize: "0.525rem",
                      fontFamily: "monospace",
                      fontWeight: count > 0 ? "bold" : "normal",
                      padding: "3px",
                      width: "22px",
                      maxWidth: "22px",
                      minWidth: "22px",
                      borderRight: "1px solid rgba(0, 0, 0, 0.2)",
                      overflow: "hidden",
                    }}
                  >
                    {count > 0 ? (
                      <Tooltip
                        title={`${tailNumbers} (click for details)`}
                        arrow
                      >
                        <span
                          onClick={handleClick}
                          style={{ cursor: "pointer", userSelect: "none" }}
                        >
                          {count}
                        </span>
                      </Tooltip>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                );
              })}
            </TableRow>

            {/* Lane Utilization Rows - dynamically extracted from spots */}
            {lanes.map((lane) => (
              <TableRow key={lane.laneId}>
                <TableCell
                  colSpan={2}
                  component="th"
                  scope="row"
                  sx={{
                    fontWeight: "bold",
                    bgcolor: "white",
                    position: "sticky",
                    left: 0,
                    zIndex: 1,
                    color: "warning.main",
                    minWidth: "165px",
                  }}
                >
                  {lane.laneName}
                </TableCell>
                {buckets.map((bucket) => {
                  const departingAircraft =
                    timelineData.laneData.get(`${lane.laneId}-${bucket}`) || [];
                  const count = departingAircraft.length;
                  const tailNumbers = departingAircraft
                    .map((ac) => ac.tail_number)
                    .join(", ");

                  return (
                    <TableCell
                      key={bucket}
                      align="center"
                      sx={{
                        bgcolor: count > 0 ? "warning.200" : "transparent",
                        color: count > 0 ? "warning.dark" : "text.disabled",
                        fontSize: "0.525rem",
                        fontFamily: "monospace",
                        fontWeight: count > 0 ? "bold" : "normal",
                        padding: "3px",
                        width: "22px",
                        maxWidth: "22px",
                        minWidth: "22px",
                        borderRight: "1px solid rgba(0, 0, 0, 0.2)",
                        overflow: "hidden",
                      }}
                    >
                      {count > 0 ? (
                        <Tooltip title={`Departing: ${tailNumbers}`} arrow>
                          <span style={{ cursor: "help" }}>{count}</span>
                        </Tooltip>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}

            {sortedSpots.map((spot) => {
              // Find spot's zone and check if disabled
              const spotZone = zones.find((z) => z.id === spot.zoneId);
              const isDisabled = !isSpotEnabled(spot, zones);
              const isSpotDirectlyDisabled = spot.disabled === true;
              const isZoneDisabled = spotZone?.disabled === true;

              // Determine disabled reason for tooltip
              let disabledReason = "";
              if (isSpotDirectlyDisabled && isZoneDisabled) {
                disabledReason = `Spot disabled AND ${
                  spotZone?.name || "Zone"
                } disabled`;
              } else if (isSpotDirectlyDisabled) {
                disabledReason = "Spot disabled (unavailable to algorithm)";
              } else if (isZoneDisabled) {
                disabledReason = `${
                  spotZone?.name || "Zone"
                } disabled (all spots in zone unavailable)`;
              }

              return (
                <TableRow key={spot.id}>
                  {/* Zone Cell */}
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      fontWeight: "normal",
                      bgcolor: "grey.50",
                      position: "sticky",
                      left: 0,
                      zIndex: 1,
                      minWidth: "60px",
                      color: isZoneDisabled
                        ? "text.disabled"
                        : "text.secondary",
                      fontSize: "0.56rem",
                    }}
                  >
                    {isZoneDisabled && "🚫 "}
                    {spotZone?.name || "—"}
                  </TableCell>

                  {/* Spot Cell */}
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      fontWeight: "bold",
                      bgcolor: "grey.50",
                      position: "sticky",
                      left: "60px",
                      zIndex: 1,
                      minWidth: "105px",
                      color: isDisabled ? "text.disabled" : "inherit",
                      textDecoration: isDisabled ? "line-through" : "none",
                    }}
                  >
                    {isDisabled && "🚫 "}
                    {isSpotInStagingZone(spot) && "🔶 "}
                    {spotIdToName?.[spot.id] || spot.id}{" "}
                    {/* Show allowed sizes for spots with restrictions (not universal)
                        WHY: Helps understand spot size policies at a glance
                        EXAMPLE: "A01 (S, M)" means spot only allows small and midsize aircraft */}
                    {spot.allowedSizes.length < 5 && (
                      <Box
                        component="span"
                        sx={{
                          fontSize: "0.49rem",
                          color: isDisabled
                            ? "text.disabled"
                            : "text.secondary",
                          fontWeight: "normal",
                        }}
                      >
                        ({formatSizeCategories(spot.allowedSizes)})
                      </Box>
                    )}
                    {isDisabled && (
                      <Tooltip title={disabledReason} arrow>
                        <Box
                          component="span"
                          sx={{
                            ml: 0.5,
                            color: "error.main",
                            fontSize: "0.49rem",
                            fontWeight: "bold",
                          }}
                        >
                          (DISABLED)
                        </Box>
                      </Tooltip>
                    )}
                  </TableCell>
                  {buckets.map((bucket) => {
                    const ac = timelineData.aircraftAtSpotMap.get(
                      `${spot.id}-${bucket}`
                    );

                    // Determine if tail number should be shown (pre-computed)
                    const blockSpan = ac
                      ? timelineData.blockSpans.get(`${spot.id}-${ac.id}`)
                      : null;
                    const showTailNumber = blockSpan
                      ? bucket === blockSpan.middle
                      : false;

                    // Check if this spot is blocked by another aircraft (pre-computed)
                    const blockingInfo = !ac
                      ? timelineData.blockingInfoMap.get(`${spot.id}-${bucket}`)
                      : null;

                    // Determine if this bucket is in a staging segment
                    const stagingInfo = ac
                      ? getStagingSegmentInfo(
                          spot.id,
                          bucket,
                          ac,
                          placement,
                          spots
                        )
                      : null;

                    // Color-code segments: orange for staging, normal colors for permanent
                    const backgroundColor = ac
                      ? stagingInfo?.isStaging
                        ? "#FF9800" // Orange for staging segments
                        : getAircraftColor(ac.id, aircraft) // Normal color for permanent
                      : "transparent";

                    // Add diagonal stripes pattern for blocked spots
                    const blockedPattern = blockingInfo
                      ? "repeating-linear-gradient(45deg, rgba(255, 152, 0, 0.3), rgba(255, 152, 0, 0.3) 2px, transparent 2px, transparent 4px)"
                      : undefined;

                    return (
                      <TableCell
                        key={bucket}
                        align="center"
                        sx={{
                          bgcolor: backgroundColor,
                          backgroundImage: blockedPattern,
                          color: ac
                            ? "white"
                            : blockingInfo
                            ? "warning.dark"
                            : "text.disabled",
                          fontSize: "0.525rem",
                          fontFamily: "monospace",
                          padding: "3px",
                          width: "22px",
                          maxWidth: "22px",
                          minWidth: "22px",
                          borderRight: "1px solid rgba(0, 0, 0, 0.2)",
                          overflow: "hidden",
                          position: "relative",
                        }}
                      >
                        {ac ? (
                          showTailNumber ? (
                            <Tooltip
                              title={
                                <Box
                                  sx={{
                                    bgcolor: "white",
                                    p: 1.5,
                                    minWidth: 400,
                                  }}
                                >
                                  <DecisionLogDisplay
                                    aircraft={ac}
                                    log={log}
                                    compact
                                    spotIdToName={spotIdToName}
                                  />
                                </Box>
                              }
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    bgcolor: "white",
                                    boxShadow: 3,
                                    maxWidth: "none",
                                    "& .MuiTooltip-arrow": {
                                      color: "white",
                                    },
                                  },
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  cursor: "help",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "100%",
                                  height: "100%",
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                }}
                              >
                                {ac.tail_number}
                                {stagingInfo?.segmentType && (
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontSize: "0.4rem",
                                      opacity: 0.8,
                                      ml: 0.2,
                                    }}
                                  >
                                    {stagingInfo.segmentType === "arrival"
                                      ? " (A)"
                                      : stagingInfo.segmentType === "departure"
                                      ? " (D)"
                                      : ""}
                                  </Typography>
                                )}
                              </Box>
                            </Tooltip>
                          ) : (
                            <Box
                              sx={{
                                width: "100%",
                                height: "100%",
                                position: "absolute",
                                top: 0,
                                left: 0,
                              }}
                            />
                          )
                        ) : blockingInfo ? (
                          <Tooltip title={blockingInfo.reason} arrow>
                            <span
                              style={{ cursor: "help", fontSize: "0.45rem" }}
                            >
                              🚫
                            </span>
                          </Tooltip>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Unplaced Aircraft Dialog */}
      <UnplacedAircraftDialog
        open={unplacedDialogOpen}
        onClose={() => setUnplacedDialogOpen(false)}
        unplacedAircraft={timelineData.unplacedData[selectedBucket] || []}
        bucket={selectedBucket}
        log={log}
        spotIdToName={spotIdToName}
        startTime={startTime}
      />
    </Paper>
  );
};
